import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, Typography, Box, Button, TextField, Select, MenuItem, FormControl, InputLabel, Divider, Autocomplete, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Paper, Snackbar, Alert, FormControlLabel, Checkbox, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, IconButton } from "@mui/material";
import { Edit as EditIcon, Add as AddIcon, Delete as DeleteIcon, Close as CloseIcon } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SalaryStructureView from "./salaryStructureView";
import { getData, postData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";

const grossSalaryChangeTypes = [
    "Annual Increment",
    "Promotional Increase",
    "Market Adjustment",
    "Others",
];

const allowanceKeyMap = {
    "Overtime Allowance (OT)": "overtime",
    "Meal / Food Allowance": "meal",
    "Shift A": "shiftA",
    "Shift B": "shiftB",
    "Shift C": "shiftC",
    "Uniform Allowance": "uniform",
    "Child Allowance": "child",
    LTA: "lta",
    "Vehicle Allowance": "vehicle",
    "Other Allowance": "other",
};

const allowanceTypes = Object.keys(allowanceKeyMap);

function HRCompensationTab({ selectedEmployeeId, onBack, onPayrollSettingsMissing }) {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const hrId = userData?.id;
    const [compensationList, setCompensationList] = useState([]);
    const [compensationListLoading, setCompensationListLoading] = useState(false);
    const [employeeCompensationLoading, setEmployeeCompensationLoading] = useState(false);
    const [addSelectedEmployee, setAddSelectedEmployee] = useState(null);
    const [addGrossSalary, setAddGrossSalary] = useState("");
    const [computedSalaryStructure, setComputedSalaryStructure] = useState(null);
    const [showSalaryStructure, setShowSalaryStructure] = useState(false);
    const [currentMode, setCurrentMode] = useState("edit");
    const [editSelectedEmployee, setEditSelectedEmployee] = useState(null);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [openOthers, setOpenOthers] = useState(false);
    const [selectedAllowanceType, setSelectedAllowanceType] = useState("");
    const [allowanceAmount, setAllowanceAmount] = useState("");
    const [otherAllowanceType, setOtherAllowanceType] = useState("");
    const [addedAllowances, setAddedAllowances] = useState([]);
    const [variablePay, setVariablePay] = useState("");
    const [openVariableDialog, setOpenVariableDialog] = useState(false);
    const [variablePayDraft, setVariablePayDraft] = useState("");
    const [activeView, setActiveView] = useState("form"); // 'form' | 'preview'
    const [showUpdateButton, setShowUpdateButton] = useState(false);
    const [openAddAllowanceConfirm, setOpenAddAllowanceConfirm] = useState(false);
    const [editGrossDialog, setEditGrossDialog] = useState(false);
    const [editGrossValue, setEditGrossValue] = useState("");
    const [editChangeType, setEditChangeType] = useState("");
    const [editCustomType, setEditCustomType] = useState("");
    const [editChangeReason, setEditChangeReason] = useState("");
    const [editEffectiveFrom, setEditEffectiveFrom] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isSavedSuccess, setIsSavedSuccess] = useState(false);
    const [isFinalized, setIsFinalized] = useState(false);
    const [isGrossSuccess, setIsGrossSuccess] = useState(false);
    const [isSubmittingAllowance, setIsSubmittingAllowance] = useState(false);
    const [editAllowanceAmountError, setEditAllowanceAmountError] = useState("");
    const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);
    const [saveMessage, setSaveMessage] = useState({
        open: false,
        text: "",
        severity: "success",
    });
    const [showOptionsSelection, setShowOptionsSelection] = useState(false);
    const [isEditingIndex, setIsEditingIndex] = useState(null);
    const [openEditAllowances, setOpenEditAllowances] = useState(false);
    const [openEditVariable, setOpenEditVariable] = useState(false);
    const [editVariablePayValue, setEditVariablePayValue] = useState(0);
    const [editAllowances, setEditAllowances] = useState([]);
    const [editSelectedAllowanceType, setEditSelectedAllowanceType] = useState("");
    const [editAllowanceAmount, setEditAllowanceAmount] = useState("");
    const [editOtherAllowanceType, setEditOtherAllowanceType] = useState("");
    const [allowanceAmountError, setAllowanceAmountError] = useState("");
    const [isPartTime, setIsPartTime] = useState(false);
    const [workingHoursPerDay, setWorkingHoursPerDay] = useState(8);

    useEffect(() => {
        const fetchCompensationEmployees = async () => {
            setCompensationListLoading(true);
            try {
                const response = await getData(API_ENDPOINTS.getPayrollsByHrId(hrId));
                const data = response?.result || [];
                setCompensationList(data);

                const redirectedId = localStorage.getItem("redirectedEmployeeId");
                const redirectMode = localStorage.getItem("redirectMode");

                if (redirectedId && redirectMode === "add") {
                    setCurrentMode("add");
                    const emp = data.find((e) => e.employeePersonalId === Number(redirectedId));
                    if (emp) {
                        setAddSelectedEmployee(emp);
                        setEditSelectedEmployee(null);
                    }
                    localStorage.removeItem("redirectedEmployeeId");
                    localStorage.removeItem("redirectMode");
                } else if (selectedEmployeeId) {
                    const emp = data.find((e) => e.employeePersonalId === Number(selectedEmployeeId));
                    if (emp) {
                        setCurrentMode("edit");
                        setEditSelectedEmployee(emp);
                    }
                } else {
                    setCurrentMode("add");
                }
            } catch (error) {
                return null;
            } finally {
                setCompensationListLoading(false);
            }
        };
        fetchCompensationEmployees();
    }, [hrId, selectedEmployeeId]);

    const fetchEmployeeCompensation = useCallback(async () => {
        const selectedEmp = currentMode === "add" ? addSelectedEmployee : editSelectedEmployee;

        if (!selectedEmp) {
            setSelectedEmployeeData(null);
            setEmployeeCompensationLoading(false);
            return;
        }

        setEmployeeCompensationLoading(true);
        try {
            const response = await getData(API_ENDPOINTS.getPayrollCompensationByEmployeeId(selectedEmp.employeePersonalId));
            setSelectedEmployeeData(response.result || response);
        } catch (error) {
            setSelectedEmployeeData(null);
        } finally {
            setEmployeeCompensationLoading(false);
        }
    }, [addSelectedEmployee, editSelectedEmployee, currentMode]);

    useEffect(() => {
        fetchEmployeeCompensation();
    }, [fetchEmployeeCompensation]);

    useEffect(() => {
        if (currentMode === "edit" && selectedEmployeeData) {
            setEditVariablePayValue(Number(selectedEmployeeData.variablePay || 0));
            setEditAllowances([]);
            setIsPartTime(selectedEmployeeData.isPartTime || false);
            setWorkingHoursPerDay(selectedEmployeeData.workingHoursPerDay || 8);
        }
    }, [currentMode, selectedEmployeeData]);

    useEffect(() => {
        if (!selectedEmployeeId) {
            setEditSelectedEmployee(null);
            setSelectedEmployeeData(null);
        }
    }, [selectedEmployeeId]);

    useEffect(() => {
        if (selectedEmployeeData?.otherAllowancesDetail) {
            const formatted = Object.entries(
                selectedEmployeeData.otherAllowancesDetail
            ).map(([key, val]) => ({
                type: key,
                amount: typeof val === "object" && val !== null ? val.amount : Number(val),
            }));
            setEditAllowances(formatted);
        }
    }, [selectedEmployeeData]);

    const [touched, setTouched] = useState({
        gross: false,
        changeType: false,
        customType: false,
        reason: false,
    });

    const handleEmployeeChange = (event, newValue) => {
        setAddSelectedEmployee(newValue);
        setAddGrossSalary("");
        setComputedSalaryStructure(null);
        setShowSalaryStructure(false);
        setAddedAllowances([]);
        setVariablePay("");
        setIsSavedSuccess(false);
        setShowOptionsSelection(false);
        const isEmpPartTime =
            newValue?.payrollBatch === "PART TIME" ||
            (!newValue?.payrollBatch &&
                (newValue?.employmentType?.toLowerCase() === "part-time" ||
                    newValue?.employmentType?.toLowerCase() === "part time"));
        setIsPartTime(isEmpPartTime);
        setWorkingHoursPerDay(isEmpPartTime ? 5 : 8);
    };

    const handleEditEmployeeChange = (event, newValue) => {
        setEditSelectedEmployee(newValue);
        setAddGrossSalary("");
        setComputedSalaryStructure(null);
        setShowSalaryStructure(false);
        setAddedAllowances([]);
        setVariablePay("");
    };

    const handleModeSwitch = async (mode) => {
        setCurrentMode(mode);
        setAddSelectedEmployee(null);
        setEditSelectedEmployee(null);
        setSelectedEmployeeData(null);
        setAddGrossSalary("");
        setComputedSalaryStructure(null);
        setShowSalaryStructure(false);
        setAddedAllowances([]);
        setVariablePay("");
        setShowOptionsSelection(false);
        setIsFinalized(false);
        setIsSavedSuccess(false);
        setShowUpdateButton(false);
        setIsPartTime(false);
        setWorkingHoursPerDay(8);
        try {
            const response = await getData(API_ENDPOINTS.getPayrollsByHrId(hrId));
            const data = response?.result || [];
            setCompensationList(data);
        } catch (error) {
        }
    };

    const handleGrossSalaryChange = (e) => {
        const val = e.target.value;
        if (/^\d*$/.test(val)) {
            setAddGrossSalary(val);
            if (val === "") {
                setComputedSalaryStructure(null);
                setShowSalaryStructure(false);
            } else {
                const gross = Number(val);
                const basicPay = Math.round(gross * 0.4);
                const hra = Math.round(gross * 0.2);
                const conveyance = 1600;
                const specialAllowance = gross - basicPay - hra - conveyance;
                setComputedSalaryStructure({
                    basicPay,
                    hra,
                    conveyance,
                    specialAllowance,
                });
                setShowSalaryStructure(false);
            }
        }
    };

    const handleOpenEditDialog = () => {
        setEditGrossValue(selectedEmployeeData?.grossEarningsMonthly || "");
        setEditChangeType("");
        setEditCustomType("");
        setEditChangeReason("");
        setEditEffectiveFrom(new Date());
        setEditGrossDialog(true);
    };

    const saveGrossSalary = () => {
        if (!addGrossSalary || isNaN(Number(addGrossSalary))) {
            alert("Please enter a valid gross salary");
            return;
        }
        setShowSalaryStructure(false);
        setVariablePay("");
        setOpenOthers(false);
        setAddedAllowances([]);
        setShowOptionsSelection(true);
    };

    const resetForm = () => {
        setSelectedAllowanceType("");
        setOtherAllowanceType("");
        setAllowanceAmount("");
        setIsEditingIndex(null);
    };

    const handleAddAllowance = async () => {
        try {
            let type = selectedAllowanceType;
            if (!type) {
                setAllowanceAmountError("Please select an allowance type.");
                return;
            }

            if (type === "Other Allowance") {
                if (!otherAllowanceType.trim()) {
                    setAllowanceAmountError("Please specify the Other Allowance type.");
                    return;
                }
                type = otherAllowanceType.trim();
            }

            const amt = Number(allowanceAmount || 0);
            if (isNaN(amt) || amt <= 0) {
                setAllowanceAmountError("Please enter a valid amount greater than 0.");
                return;
            }

            setAllowanceAmountError("");

            const normalizeKey = (label) =>
                allowanceKeyMap[label.trim()] ||
                label.toLowerCase().replace(/[^a-z0-9]/gi, "");

            const newItemKey = normalizeKey(type);
            const isDuplicate =
                isEditingIndex === null &&
                addedAllowances.some((a) => normalizeKey(a.type) === newItemKey);

            if (isDuplicate) {
                setAllowanceAmountError("This allowance type is already added.");
                return;
            }

            const updatedList = [...addedAllowances];
            const newAllowance = { type, amount: amt };

            if (isEditingIndex !== null) {
                updatedList[isEditingIndex] = newAllowance;
                setIsEditingIndex(null);
            } else {
                updatedList.push(newAllowance);
            }

            const allowancesPayload = updatedList.reduce((acc, a) => {
                const key = normalizeKey(a.type);
                acc[key] = Number(a.amount || 0);
                return acc;
            }, {});

            const basicPayForValidation = Number(
                selectedEmployeeData?.basicPayMonthly ||
                computedSalaryStructure?.basicPay ||
                0
            );

            const payload = {
                basicPay: basicPayForValidation,
                allowances: allowancesPayload,
            };

            const validationRes = await postData(API_ENDPOINTS.calculatePayrollAllowances, payload);
            if (!validationRes || validationRes.status !== "SUCCESS") {
                setAllowanceAmountError(
                    validationRes?.message || "Failed to validate allowances."
                );
                return;
            }

            const results = validationRes.results || {};
            const selectedValidation = results[newItemKey] || results["other"];

            if (selectedValidation && selectedValidation.isValid === false) {
                setAllowanceAmountError(
                    selectedValidation.errorMessage || "Invalid allowance amount."
                );
                return;
            }

            setAddedAllowances(updatedList);
            setSelectedAllowanceType("");
            setOtherAllowanceType("");
            setAllowanceAmount("");
            setAllowanceAmountError("");

            const updatedAllowancesObj = updatedList.reduce((acc, a) => {
                acc[a.type] = Number(a.amount || 0);
                return acc;
            }, {});

            const totalOtherAllowances = Object.values(updatedAllowancesObj).reduce(
                (sum, val) => sum + Number(val || 0),
                0
            );

            setSelectedEmployeeData((prev) => {
                const basic = Number(prev?.basicPayMonthly || 0);
                const hra = Number(prev?.hraMonthly || 0);
                const conv = Number(prev?.conveyanceAllowanceMonthly || 0);
                const spl = Number(prev?.specialAllowanceMonthly || 0);
                const deductions = Number(prev?.totalDeductionsMonthly || 0);
                const variable = Number(prev?.variablePay || 0);
                const grossMonthly = basic + hra + conv + spl + totalOtherAllowances;
                const grossAnnual = grossMonthly * 12;

                return {
                    ...prev,
                    otherAllowancesDetail: updatedAllowancesObj,
                    otherAllowanceMonthly: totalOtherAllowances,
                    grossEarningsMonthly: grossMonthly,
                    grossEarningsAnnual: grossAnnual,
                    netSalaryMonthly: grossMonthly - deductions + variable,
                    annualCTC: grossAnnual + Number(prev?.pfEmployerAnnual || 0) + Number(prev?.esiEmployerAnnual || 0) + Number(prev?.gratuityAccrualAnnual || 0) + variable,
                };
            });
            setOpenOthers(false);
        } catch (err) {
            setAllowanceAmountError("Network or validation error. Please try again.");
        }
    };

    const handleEditAllowance = (index) => {
        const allowance = addedAllowances[index];
        setSelectedAllowanceType(
            allowanceTypes.includes(allowance.type)
                ? allowance.type
                : "Other Allowance"
        );
        if (!allowanceTypes.includes(allowance.type)) {
            setOtherAllowanceType(allowance.type);
        } else {
            setOtherAllowanceType("");
        }
        setAllowanceAmount(allowance.amount.toString());
        setIsEditingIndex(index);
    };

    const handleDeleteAllowance = (index) => {
        const newList = addedAllowances.filter((_, i) => i !== index);
        setAddedAllowances(newList);
        resetForm();
    };

    const handlePreviewOpen = () => {
        if (!isSavedSuccess && !isFinalized) {
            setSaveMessage({
                open: true,
                text: "Please confirm the salary structure before opening the preview.",
                severity: "warning",
            });
            return;
        }
        setActiveView("preview");
    };

    const handleConfirmCompensation = async () => {
        if (!addSelectedEmployee || !addGrossSalary) {
            setSaveMessage({
                open: true,
                text: "Please select employee and enter gross salary",
                severity: "error",
            });
            return;
        }

        setEmployeeCompensationLoading(true);

        try {
            const payload = {
                employeePersonalId: addSelectedEmployee.employeePersonalId,
                hr_id: Number(hrId),
                grossEarnings: Number(addGrossSalary),
                variablePay: Number(variablePay) || 0,
                isPartTime,
                workingHoursPerDay: Number(workingHoursPerDay),
                otherAllowancesDetail: addedAllowances.reduce((acc, allowance) => {
                    acc[allowance.type] = allowance.amount;
                    return acc;
                }, {}),
            };

            const result = await postData(API_ENDPOINTS.createPayrollCompensation, payload);

            const isPayrollSettingsMissing =
                result?.statusCode === 400 &&
                typeof result?.message === "string" &&
                result.message
                    .toLowerCase()
                    .includes("payroll settings not found for this employee");

            if (isPayrollSettingsMissing) {
                const employeeId = addSelectedEmployee?.employeePersonalId;
                if (employeeId) {
                    localStorage.setItem("redirectedEmployeeId", String(employeeId));
                    localStorage.setItem("redirectMode", "add");
                }
                setSaveMessage({
                    open: true,
                    text: "Payroll settings not found for this employee",
                    severity: "error",
                });
                setIsSuccess(false);
                setIsSavedSuccess(false);
                handlePayrollSettingsMissingRedirect(employeeId);
                return false;
            } else if (result.success === true || result.status === "SUCCESS") {
                setIsSuccess(true);
                setIsSavedSuccess(true);

                setSaveMessage({
                    open: true,
                    text: "Compensation saved successfully!",
                    severity: "success",
                });
                return true;
            } else {
                setIsSuccess(false);
                setIsSavedSuccess(false);

                setSaveMessage({
                    open: true,
                    text: result.message || "Failed to save compensation. Please try again.",
                    severity: "error",
                });
                return false;
            }
        } catch (error) {
            setIsSuccess(false);
            setIsSavedSuccess(false);

            setSaveMessage({
                open: true,
                text: "An error occurred while saving.",
                severity: "error",
            });
            return false;
        } finally {
            setEmployeeCompensationLoading(false);
        }
    };

    const handlePayrollSettingsMissingRedirect = (employeeId) => {
        if (!employeeId || typeof onPayrollSettingsMissing !== "function") return;
        setTimeout(() => {
            onPayrollSettingsMissing(employeeId);
        }, 1200);
    };

    const handleUpdateCompensation = async () => {
        try {
            const allowancesSource =
                editAllowances.length > 0
                    ? editAllowances
                    : Object.entries(
                        selectedEmployeeData?.otherAllowancesDetail || {}
                    ).map(([type, val]) => ({
                        type,
                        amount:
                            typeof val === "object" && val !== null
                                ? Number(val.amount || 0)
                                : Number(val || 0),
                    }));

            const otherAllowancesDetail = allowancesSource.reduce((acc, a) => {
                acc[a.type] = Number(a.amount || 0);
                return acc;
            }, {});

            const payload = {
                id: selectedEmployeeData?.id,
                employeePersonalId: selectedEmployeeData?.employeePersonalId,
                hr_id: selectedEmployeeData?.hr_id,

                grossEarnings: Number(
                    editGrossValue !== "" && editGrossValue !== null
                        ? editGrossValue
                        : selectedEmployeeData?.grossEarningsMonthly || 0
                ),

                variablePay:
                    editVariablePayValue !== undefined && editVariablePayValue !== null
                        ? Number(editVariablePayValue)
                        : Number(selectedEmployeeData?.variablePay || 0),

                change_type:
                    (editChangeType === "Others"
                        ? editCustomType || "Other"
                        : editChangeType) || "increment",

                effectiveFrom:
                    editEffectiveFrom instanceof Date
                        ? editEffectiveFrom.toISOString().split("T")[0]
                        : selectedEmployeeData?.Effective_From ||
                        new Date().toISOString().split("T")[0],

                effectiveFromReason: editChangeReason || "Compensation Update",
                isPartTime,
                workingHoursPerDay: Number(workingHoursPerDay),
                otherAllowancesDetail,
            };

            const response = await postData(API_ENDPOINTS.createPayrollCompensation, payload);

            if (response?.success || response?.status === "SUCCESS") {
                setSaveMessage({
                    open: true,
                    text: "Compensation updated successfully!",
                    severity: "success",
                });
                setShowUpdateButton(false);
                await fetchEmployeeCompensation();
            } else {
                const errorMsg =
                    response?.error?.message ||
                    response?.message ||
                    "Failed to update compensation. Please try again.";
                setSaveMessage({ open: true, text: errorMsg, severity: "error" });
            }
        } catch (error) {
            setSaveMessage({
                open: true,
                text: "Something went wrong while updating compensation.",
                severity: "error",
            });
        }
    };

    const handleSaveGrossSalary = async () => {
        if (!addSelectedEmployee || !addGrossSalary) {
            setSaveMessage({
                open: true,
                text: "Please select employee and enter gross salary",
                severity: "error",
            });
            return;
        }
        setEmployeeCompensationLoading(true);

        try {
            const payload = {
                employeePersonalId: addSelectedEmployee.employeePersonalId,
                hr_id: hrId,
                grossEarnings: Number(addGrossSalary),
                variablePay: Number(variablePay) || 0,
                otherAllowancesDetail: addedAllowances.reduce((acc, allowance) => {
                    acc[allowance.type] = allowance.amount;
                    return acc;
                }, {}),
            };

            const result = await postData(API_ENDPOINTS.previewPayrollCompensation, payload);

            const isPayrollSettingsMissing =
                result?.statusCode === 400 &&
                typeof result?.message === "string" &&
                result.message
                    .toLowerCase()
                    .includes("payroll settings not found for this employee");

            if (isPayrollSettingsMissing) {
                const employeeId = addSelectedEmployee?.employeePersonalId;
                if (employeeId) {
                    localStorage.setItem("redirectedEmployeeId", String(employeeId));
                    localStorage.setItem("redirectMode", "add");
                }

                setSaveMessage({
                    open: true,
                    text: "Payroll settings not found for this employee",
                    severity: "error",
                });
                setIsGrossSuccess(false);
                handlePayrollSettingsMissingRedirect(employeeId);
            } else if (result.success === true || result.status === "SUCCESS") {
                setIsGrossSuccess(true);
                setSaveMessage({
                    open: true,
                    text: "Gross Salary saved successfully!",
                    severity: "success",
                });
            } else {
                setIsGrossSuccess(false);
                setSaveMessage({
                    open: true,
                    text: result.message || "Failed to save compensation. Please try again.",
                    severity: "error",
                });
            }
        } catch (error) {
            setIsGrossSuccess(false);
            setSaveMessage({
                open: true,
                text: "An error occurred while saving.",
                severity: "error",
            });
        } finally {
            setEmployeeCompensationLoading(false);
        }
    };

    const handleSaveAndContinue = async () => {
        await saveGrossSalary();
        await handleSaveGrossSalary();
    };

    const isCompensationPageLoading =
        compensationListLoading || employeeCompensationLoading;

    if (isCompensationPageLoading) {
        return (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Card sx={{ borderRadius: 3, boxShadow: 4, width: "100%" }}>
                    <CardContent sx={{ px: 3 }}>
                        <Box
                            sx={{
                                minHeight: 260,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CircularProgress size={28} />
                        </Box>
                    </CardContent>
                </Card>
            </LocalizationProvider>
        );
    }

    return (
        <Box sx={{ p: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Card sx={{ borderRadius: 3, boxShadow: 4, width: "100%" }}>
                    <CardContent sx={{ px: 3 }}>
                        {selectedEmployeeId && (
                            <Button
                                onClick={onBack}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 600,
                                    color: "#1976d2",
                                    backgroundColor: "transparent",
                                    "&:hover": { backgroundColor: "#e3f2fd" },
                                    mb: 1,
                                    px: 0,
                                }}
                            >
                                Back
                            </Button>
                        )}
                        {activeView === "form" && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 3,
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 700,
                                        color: "#1565c0",
                                        letterSpacing: 1.2,
                                    }}
                                >
                                    {currentMode === "edit"
                                        ? "Edit Employee Compensation"
                                        : "Add New Employee Compensation"}
                                </Typography>

                                <Button
                                    variant={currentMode === "edit" ? "outlined" : "contained"}
                                    color="primary"
                                    onClick={async () => {
                                        const newMode = currentMode === "edit" ? "add" : "edit";
                                        await handleModeSwitch(newMode);
                                    }}
                                    sx={{ fontWeight: 600, textTransform: "none" }}
                                >
                                    {currentMode === "edit" ? "Add New" : "Edit Mode"}
                                </Button>
                            </Box>
                        )}

                        {currentMode === "edit" &&
                            !selectedEmployeeId &&
                            activeView !== "preview" && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                        Select Employee to Edit
                                    </Typography>
                                    <Autocomplete
                                        options={
                                            compensationList?.filter((emp) => emp.id !== null) || []
                                        }
                                        getOptionLabel={(option) =>
                                            `${option?.first_name} ${option?.last_name} (${option?.emp_id})`
                                        }
                                        value={editSelectedEmployee}
                                        onChange={handleEditEmployeeChange}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select Employee"
                                                size="small"
                                            />
                                        )}
                                        filterSelectedOptions
                                        sx={{ width: "100%" }}
                                    />
                                </Box>
                            )}

                        {currentMode === "add" && activeView === "form" && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                    Select Employee to Add Compensation
                                </Typography>
                                <Autocomplete
                                    options={
                                        compensationList?.filter((emp) => emp.id === null) || []
                                    }
                                    getOptionLabel={(option) =>
                                        `${option?.first_name} ${option?.last_name} (${option?.emp_id})`
                                    }
                                    value={addSelectedEmployee}
                                    onChange={handleEmployeeChange}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Select Employee" size="small" />
                                    )}
                                    filterSelectedOptions
                                    sx={{ width: "100%" }}
                                />
                                {addSelectedEmployee && (
                                    <Box sx={{ mt: 3 }}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{ fontWeight: 700, mb: 1 }}
                                        >
                                            Enter Gross Salary (Monthly ₹)
                                        </Typography>

                                        <TextField
                                            label="Gross Salary"
                                            type="text"
                                            size="small"
                                            value={addGrossSalary}
                                            onChange={!isSuccess ? handleGrossSalaryChange : undefined}
                                            fullWidth
                                            InputProps={{
                                                readOnly: isSuccess,
                                                endAdornment: isSuccess ? (
                                                    <IconButton
                                                        onClick={() => {
                                                            setIsSuccess(false);
                                                            setIsSavedSuccess(false);
                                                            setShowOptionsSelection(true);
                                                        }}
                                                        edge="end"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                ) : null,
                                            }}
                                            disabled={isSuccess}
                                        />

                                        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 3 }}>
                                            {(() => {
                                                const profileIsPartTime =
                                                    addSelectedEmployee?.payrollBatch === "PART TIME" ||
                                                    (!addSelectedEmployee?.payrollBatch &&
                                                        (addSelectedEmployee?.employmentType?.toLowerCase() === "part-time" ||
                                                            addSelectedEmployee?.employmentType?.toLowerCase() === "part time"));
                                                const isChecked = profileIsPartTime ? isPartTime : !isPartTime;

                                                return (
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={isChecked}
                                                                onChange={(e) => {
                                                                    const newChecked = e.target.checked;
                                                                    const newIsPartTime = profileIsPartTime
                                                                        ? newChecked
                                                                        : !newChecked;
                                                                    setIsPartTime(newIsPartTime);
                                                                    if (newIsPartTime) setWorkingHoursPerDay(5);
                                                                    else setWorkingHoursPerDay(8);
                                                                }}
                                                                disabled={isSuccess}
                                                            />
                                                        }
                                                        label={
                                                            <Box>
                                                                <Typography>
                                                                    {profileIsPartTime
                                                                        ? "Part-time Employee"
                                                                        : "Full-time Employee"}
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                >
                                                                    (Derived from employee profile)
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                );
                                            })()}
                                            {isPartTime && (
                                                <TextField
                                                    label="Working Hours Per Day"
                                                    type="number"
                                                    size="small"
                                                    inputProps={{ min: 1, max: 24, step: 0.5 }}
                                                    value={workingHoursPerDay}
                                                    onChange={(e) => setWorkingHoursPerDay(e.target.value)}
                                                    disabled={isSuccess}
                                                    sx={{ width: 150 }}
                                                />
                                            )}
                                        </Box>

                                        {!showSalaryStructure &&
                                            addGrossSalary &&
                                            !isGrossSuccess && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<AddIcon />}
                                                        onClick={handleSaveAndContinue}
                                                        sx={{ fontWeight: 600, fontSize: 12, textTransform: "none" }}
                                                    >
                                                        Save & Continue
                                                    </Button>
                                                </Box>
                                            )}
                                    </Box>
                                )}
                            </Box>
                        )}

                        {currentMode === "add" &&
                            activeView === "form" &&
                            showOptionsSelection &&
                            isGrossSuccess &&
                            !isSuccess && (
                                <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                                        Choose additional components
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={() => setOpenOthers(true)}
                                            sx={{ textTransform: "none" }}
                                        >
                                            Add Allowances
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={() => setOpenVariableDialog(true)}
                                            sx={{ textTransform: "none" }}
                                        >
                                            Add Variable Pay
                                        </Button>
                                    </Box>
                                    <Box
                                        sx={{
                                            mt: 2,
                                            display: "flex",
                                            gap: 4,
                                            flexWrap: "wrap",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        {addedAllowances.length > 0 && (
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    minWidth: "320px",
                                                    backgroundColor: "#fafafa",
                                                    borderRadius: 2,
                                                    p: 2,
                                                    border: "1px solid #eee",
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ fontWeight: 700, mb: 1, color: "#0d47a1" }}
                                                >
                                                    Added Allowances
                                                </Typography>
                                                <Table
                                                    size="small"
                                                    sx={{
                                                        "& th": {
                                                            backgroundColor: "#f5f5f5",
                                                            color: "#333",
                                                            fontWeight: 600,
                                                            fontSize: 13,
                                                            borderBottom: "1px solid #ddd",
                                                        },
                                                        "& td": {
                                                            fontSize: 13,
                                                            color: "#444",
                                                            borderBottom: "1px solid #eee",
                                                        },
                                                    }}
                                                >
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Type</TableCell>
                                                            <TableCell>Amount (₹)</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {addedAllowances.map((a, i) => (
                                                            <TableRow key={i}>
                                                                <TableCell>{a.type}</TableCell>
                                                                <TableCell>{a.amount}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                        )}

                                        {variablePay && (
                                            <Box
                                                sx={{
                                                    flex: 0.4,
                                                    minWidth: "240px",
                                                    backgroundColor: "#fafafa",
                                                    borderRadius: 2,
                                                    p: 2,
                                                    border: "1px solid #eee",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ fontWeight: 700, mb: 1, color: "#0d47a1" }}
                                                >
                                                    Variable Pay
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: "1.1rem",
                                                        fontWeight: 600,
                                                        color: "#333",
                                                    }}
                                                >
                                                    ₹{variablePay}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={async () => {
                                                await handleConfirmCompensation();
                                            }}
                                            sx={{ textTransform: "none" }}
                                        >
                                            Confirm
                                        </Button>
                                    </Box>
                                </Paper>
                            )}

                        {currentMode === "add" &&
                            activeView === "form" &&
                            isSuccess &&
                            selectedEmployeeData && (
                                <Paper variant="outlined" sx={{ mt: 3, p: 2, borderRadius: 2 }}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 700, mb: 2, color: "#0d47a1" }}
                                    >
                                        Salary Structure
                                    </Typography>

                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography fontWeight={600}>Employment Type:</Typography>
                                            <Typography>
                                                {selectedEmployeeData.employmentType || (selectedEmployeeData.isPartTime ? "Part-time" : "Full-time")}
                                            </Typography>
                                        </Grid>
                                        {selectedEmployeeData.isPartTime && (
                                            <Grid size={{ xs: 6 }}>
                                                <Typography fontWeight={600}>Working Hours:</Typography>
                                                <Typography>
                                                    {selectedEmployeeData.workingHoursPerDay} hrs/day
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>

                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        {Number(selectedEmployeeData?.basicPayMonthly || 0) > 0 && (
                                            <Grid size={{ xs: 6 }}>
                                                <Typography fontWeight={600}>Basic Pay:</Typography>
                                                <Typography>
                                                    ₹{Number(selectedEmployeeData.basicPayMonthly).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Grid>
                                        )}
                                        {Number(selectedEmployeeData?.hraMonthly || 0) > 0 && (
                                            <Grid size={{ xs: 6 }}>
                                                <Typography fontWeight={600}>HRA:</Typography>
                                                <Typography>
                                                    ₹{Number(selectedEmployeeData.hraMonthly).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Grid>
                                        )}
                                        {Number(selectedEmployeeData?.conveyanceAllowanceMonthly || 0) > 0 && (
                                            <Grid size={{ xs: 6 }}>
                                                <Typography fontWeight={600}>Conveyance Allowance:</Typography>
                                                <Typography>
                                                    ₹{Number(selectedEmployeeData.conveyanceAllowanceMonthly).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Grid>
                                        )}
                                        {Number(selectedEmployeeData?.specialAllowanceMonthly || 0) > 0 && (
                                            <Grid size={{ xs: 6 }}>
                                                <Typography fontWeight={600}>Special Allowance:</Typography>
                                                <Typography>
                                                    ₹{Number(selectedEmployeeData.specialAllowanceMonthly).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>

                                    {selectedEmployeeData?.otherAllowancesDetail &&
                                        Object.keys(selectedEmployeeData.otherAllowancesDetail)
                                            .length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "start",
                                                        mb: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ fontWeight: 700 }}
                                                    >
                                                        Added Allowances
                                                    </Typography>

                                                    {!isFinalized && !isSavedSuccess && (
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => setOpenOthers(true)}
                                                            sx={{ ml: 1 }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Box>

                                                {Object.entries(
                                                    selectedEmployeeData.otherAllowancesDetail
                                                ).map(([type, amount], idx) => (
                                                    <Box
                                                        key={idx}
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                            mb: 0.3,
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{ fontWeight: 500, fontSize: "1rem" }}
                                                        >
                                                            {type}: ₹{Number(amount)}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}

                                    <Divider sx={{ my: 2 }} />

                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 700, mb: 2, color: "#0d47a1" }}
                                    >
                                        Deductions
                                    </Typography>
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        {Number(selectedEmployeeData?.pfEmployeeMonthly || 0) > 0 && (
                                            <Grid size={{ xs: 6 }}>
                                                <Typography>Provident Fund (PF):</Typography>
                                                <Typography>
                                                    ₹{Number(selectedEmployeeData.pfEmployeeMonthly).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Grid>
                                        )}
                                        {Number(selectedEmployeeData?.esiEmployeeMonthly || 0) > 0 && (
                                            <Grid size={{ xs: 6 }}>
                                                <Typography>Employee State Insurance (ESI):</Typography>
                                                <Typography>
                                                    ₹{Number(selectedEmployeeData?.esiEmployeeMonthly || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Grid>
                                        )}
                                        {Number(selectedEmployeeData?.professionalTaxMonthly || 0) > 0 && (
                                            <Grid size={{ xs: 6 }}>
                                                <Typography>Professional Tax (PT):</Typography>
                                                <Typography>
                                                    ₹{Number(selectedEmployeeData?.professionalTaxMonthly || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Grid>
                                        )}

                                        <Grid size={{ xs: 12 }}>
                                            <Box sx={{ border: "1px solid #e3eaf5", borderRadius: 2, p: 1.5, bgcolor: "#f5f8ff", mt: 0.5 }}>
                                                <Typography fontWeight={700} sx={{ color: "#1565c0", mb: 1 }}>
                                                    Tax Deducted at Source (TDS)
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="body2" color="text.secondary">Income Tax (Monthly):</Typography>
                                                        <Typography fontWeight={600}>
                                                            ₹{(Number(selectedEmployeeData?.monthlyTDS || 0) - Number(selectedEmployeeData?.monthlyCess || 0) - Number(selectedEmployeeData?.monthlySurcharge || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="body2" color="text.secondary">Education Cess @ 4% (Monthly):</Typography>
                                                        <Typography fontWeight={600}>
                                                            ₹{Number(selectedEmployeeData?.monthlyCess || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="body2" color="text.secondary">Surcharge (Monthly):</Typography>
                                                        <Typography fontWeight={600}>
                                                            ₹{Number(selectedEmployeeData?.monthlySurcharge || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="body2" color="text.secondary">Income Tax (Annual):</Typography>
                                                        <Typography fontWeight={600}>
                                                            ₹{(Number(selectedEmployeeData?.annualTDS || 0) - Number(selectedEmployeeData?.annualCess || 0) - Number(selectedEmployeeData?.annualSurcharge || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="body2" color="text.secondary">Education Cess @ 4% (Annual):</Typography>
                                                        <Typography fontWeight={600}>
                                                            ₹{Number(selectedEmployeeData?.annualCess || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="body2" color="text.secondary">Surcharge (Annual):</Typography>
                                                        <Typography fontWeight={600}>
                                                            ₹{Number(selectedEmployeeData?.annualSurcharge || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="body2" color="text.secondary">Marginal Relief (Annual):</Typography>
                                                        <Typography fontWeight={600} color="green">
                                                            -₹{Number(selectedEmployeeData?.marginalRelief || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 12 }}>
                                                        <Divider sx={{ my: 0.5 }} />
                                                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                                                            <Typography variant="body2" fontWeight={700}>Total TDS (Monthly):</Typography>
                                                            <Typography fontWeight={700} color="#1565c0">
                                                                ₹{Number(selectedEmployeeData?.monthlyTDS || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.3 }}>
                                                            <Typography variant="body2" fontWeight={700}>Total TDS (Annual):</Typography>
                                                            <Typography fontWeight={700} color="#1565c0">
                                                                ₹{Number(selectedEmployeeData?.annualTDS || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ my: 2 }} />

                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#0d47a1" }}>
                                        Employer Contributions
                                    </Typography>

                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        {Number(selectedEmployeeData?.pfEmployerMonthly || 0) > 0 && (
                                            <Grid size={{ xs: 6 }}>
                                                <Typography>Employer Provident Fund (PF):</Typography>
                                                <Typography>
                                                    ₹{Number(selectedEmployeeData.pfEmployerMonthly).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Grid>
                                        )}

                                        {Number(selectedEmployeeData?.employerContributions?.esi || 0) > 0 && (
                                            <Grid size={{ xs: 6 }}>
                                                <Typography>Employer ESI:</Typography>
                                                <Typography>
                                                    ₹{Number(selectedEmployeeData.employerContributions.esi).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Grid>
                                        )}

                                        {Number(selectedEmployeeData?.gratuityAccrualMonthly || 0) > 0 && (
                                            <Grid size={{ xs: 6 }}>
                                                <Typography>Gratuity:</Typography>
                                                <Typography>
                                                    ₹
                                                    {Number(selectedEmployeeData?.gratuityAccrualMonthly).toLocaleString(
                                                        "en-IN",
                                                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                                    )}
                                                </Typography>
                                            </Grid>
                                        )}

                                        {Number(selectedEmployeeData?.employerContributions?.bonus || 0) > 0 && (
                                            <Grid size={{ xs: 6 }}>
                                                <Typography>Bonus:</Typography>
                                                <Typography>
                                                    ₹
                                                    {Number(selectedEmployeeData?.employerContributions?.bonus).toLocaleString(
                                                        "en-IN",
                                                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                                    )}
                                                </Typography>
                                            </Grid>
                                        )}

                                        {Number(selectedEmployeeData?.variablePay || 0) > 0 && (
                                            <Grid size={{ xs: 6 }}>
                                                <Typography fontWeight={600}>Variable Pay:</Typography>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Typography>
                                                        ₹
                                                        {Number(selectedEmployeeData?.variablePay).toLocaleString("en-IN", {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        })}
                                                    </Typography>
                                                    {!isFinalized && !isSavedSuccess && (
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => setOpenVariableDialog(true)}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>

                                    <Divider sx={{ my: 2 }} />

                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#0d47a1" }}>
                                        Summary
                                    </Typography>
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography>Gross Salary:</Typography>
                                            <Typography>
                                                ₹{Number(selectedEmployeeData?.grossEarningsMonthly || 0).toLocaleString(
                                                    "en-IN",
                                                    { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography>Total Deductions:</Typography>
                                            <Typography>
                                                ₹
                                                {Number(
                                                    selectedEmployeeData?.totalDeductionsMonthly || 0).toLocaleString(
                                                        "en-IN",
                                                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                                    )}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography>Net Salary:</Typography>
                                            <Typography>
                                                ₹{Number(selectedEmployeeData?.netSalaryMonthly || 0).toLocaleString(
                                                    "en-IN",
                                                    { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography>Annual CTC:</Typography>
                                            <Typography>
                                                ₹
                                                {Number(
                                                    selectedEmployeeData?.annualCTC || 0).toLocaleString(
                                                        "en-IN",
                                                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                                    ).toLocaleString("en-IN")}
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color={isSavedSuccess ? "secondary" : "primary"}
                                            disabled={isSavedSuccess}
                                            onClick={() => {
                                                if (!isSavedSuccess) {
                                                    setOpenConfirmDialog(true);
                                                }
                                            }}
                                            sx={{
                                                fontWeight: 600,
                                                textTransform: "none",
                                                px: 2.5,
                                                borderRadius: 2,
                                                ...(isSavedSuccess && {
                                                    backgroundColor: "#9e9e9e",
                                                    color: "#fff",
                                                }),
                                            }}
                                        >
                                            {isSavedSuccess ? "Saved" : "Confirm"}
                                        </Button>
                                    </Box>

                                    <Dialog
                                        open={openConfirmDialog}
                                        onClose={() => setOpenConfirmDialog(false)}
                                        maxWidth="sm"
                                        fullWidth
                                    >
                                        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, color: "#1565c0" }}>
                                            <Typography variant="h6" component="span">
                                                Confirm Allowance Addition
                                            </Typography>
                                            <IconButton onClick={() => setOpenConfirmDialog(false)}>
                                                <CloseIcon />
                                            </IconButton>
                                        </DialogTitle>

                                        <DialogContent dividers>
                                            <Typography sx={{ mb: 2, color: "#555" }}>
                                                Review the salary details below. Gross salary is locked once confirmed.
                                            </Typography>

                                            <Box sx={{ bgcolor: "#f5f8ff", borderRadius: 2, p: 1.5, mb: 1.5 }}>
                                                <Typography fontWeight={700} sx={{ color: "#1565c0", mb: 1 }}>Earnings</Typography>
                                                {[
                                                    { label: "Basic Pay", val: selectedEmployeeData?.basicPayMonthly },
                                                    { label: "HRA", val: selectedEmployeeData?.hraMonthly },
                                                    { label: "Conveyance Allowance", val: selectedEmployeeData?.conveyanceAllowanceMonthly },
                                                    { label: "Special Allowance", val: selectedEmployeeData?.specialAllowanceMonthly },
                                                ].filter(r => Number(r.val || 0) > 0).map((r, i) => (
                                                    <Box key={i} sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                                                        <Typography variant="body2">{r.label}</Typography>
                                                        <Typography variant="body2" fontWeight={600}>₹{Number(r.val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                                    </Box>
                                                ))}
                                                {selectedEmployeeData?.otherAllowancesDetail && Object.entries(selectedEmployeeData.otherAllowancesDetail).filter(([, v]) => Number(v || 0) > 0).map(([type, amt], i) => (
                                                    <Box key={`oa-${i}`} sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                                                        <Typography variant="body2">{type}</Typography>
                                                        <Typography variant="body2" fontWeight={600}>₹{Number(amt).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                                    </Box>
                                                ))}
                                                <Divider sx={{ my: 0.8 }} />
                                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                    <Typography variant="body2" fontWeight={700}>Gross Salary (Monthly)</Typography>
                                                    <Typography variant="body2" fontWeight={700} color="#1565c0">₹{Number(selectedEmployeeData?.grossEarningsMonthly || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                                </Box>
                                            </Box>

                                            <Box sx={{ bgcolor: "#fff8f8", borderRadius: 2, p: 1.5, mb: 1.5 }}>
                                                <Typography fontWeight={700} sx={{ color: "#c62828", mb: 1 }}>Deductions</Typography>
                                                {[
                                                    { label: "Provident Fund (PF)", val: selectedEmployeeData?.pfEmployeeMonthly },
                                                    { label: "Employee State Insurance (ESI)", val: selectedEmployeeData?.esiEmployeeMonthly },
                                                    { label: "Professional Tax (PT)", val: selectedEmployeeData?.professionalTaxMonthly },
                                                ].filter(r => Number(r.val || 0) > 0).map((r, i) => (
                                                    <Box key={i} sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                                                        <Typography variant="body2">{r.label}</Typography>
                                                        <Typography variant="body2" fontWeight={600}>₹{Number(r.val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                                    </Box>
                                                ))}
                                                {Number(selectedEmployeeData?.monthlyTDS || 0) > 0 && (
                                                    <Box sx={{ mt: 0.5, pl: 0 }}>
                                                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.3 }}>Tax Deducted at Source (TDS):</Typography>
                                                        {(Number(selectedEmployeeData?.monthlyTDS || 0) - Number(selectedEmployeeData?.monthlyCess || 0)) > 0 && (
                                                            <Box sx={{ display: "flex", justifyContent: "space-between", pl: 1.5, mb: 0.2 }}>
                                                                <Typography variant="body2" color="text.secondary">↳ Income Tax</Typography>
                                                                <Typography variant="body2">₹{(Number(selectedEmployeeData.monthlyTDS) - Number(selectedEmployeeData?.monthlyCess || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                                            </Box>
                                                        )}
                                                        {Number(selectedEmployeeData?.monthlyCess || 0) > 0 && (
                                                            <Box sx={{ display: "flex", justifyContent: "space-between", pl: 1.5, mb: 0.2 }}>
                                                                <Typography variant="body2" color="text.secondary">↳ Education Cess @ 4%</Typography>
                                                                <Typography variant="body2">₹{Number(selectedEmployeeData.monthlyCess).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                                            </Box>
                                                        )}
                                                        {Number(selectedEmployeeData?.monthlySurcharge || 0) > 0 && (
                                                            <Box sx={{ display: "flex", justifyContent: "space-between", pl: 1.5, mb: 0.2 }}>
                                                                <Typography variant="body2" color="text.secondary">↳ Surcharge</Typography>
                                                                <Typography variant="body2">₹{Number(selectedEmployeeData.monthlySurcharge).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                                            </Box>
                                                        )}
                                                        {Number(selectedEmployeeData?.marginalRelief || 0) > 0 && (
                                                            <Box sx={{ display: "flex", justifyContent: "space-between", pl: 1.5, mb: 0.2 }}>
                                                                <Typography variant="body2" color="text.secondary">↳ Marginal Relief</Typography>
                                                                <Typography variant="body2" color="green">-₹{Number(selectedEmployeeData.marginalRelief).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                                            </Box>
                                                        )}
                                                        <Box sx={{ display: "flex", justifyContent: "space-between", pl: 1.5 }}>
                                                            <Typography variant="body2" fontWeight={600}>↳ Total TDS</Typography>
                                                            <Typography variant="body2" fontWeight={600}>₹{Number(selectedEmployeeData.monthlyTDS).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                                <Divider sx={{ my: 0.8 }} />
                                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                    <Typography variant="body2" fontWeight={700}>Total Deductions (Monthly)</Typography>
                                                    <Typography variant="body2" fontWeight={700} color="#c62828">₹{Number(selectedEmployeeData?.totalDeductionsMonthly || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                                </Box>
                                            </Box>

                                            <Box sx={{ bgcolor: "#f0faf0", borderRadius: 2, p: 1.5 }}>
                                                <Typography fontWeight={700} sx={{ color: "#2e7d32", mb: 1 }}>Summary</Typography>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                                                    <Typography variant="body2">Net Take Home (Monthly)</Typography>
                                                    <Typography variant="body2" fontWeight={700} color="#2e7d32">₹{Number(selectedEmployeeData?.netSalaryMonthly || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                                </Box>
                                                {Number(selectedEmployeeData?.variablePay || 0) > 0 && (
                                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                                                        <Typography variant="body2">Variable Pay (Annual)</Typography>
                                                        <Typography variant="body2" fontWeight={600}>₹{Number(selectedEmployeeData.variablePay).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                                    </Box>
                                                )}
                                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                    <Typography variant="body2" fontWeight={700}>Annual CTC</Typography>
                                                    <Typography variant="body2" fontWeight={700} color="#1565c0">₹{Number(selectedEmployeeData?.annualCTC || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                                </Box>
                                            </Box>
                                        </DialogContent>

                                        <DialogActions sx={{ justifyContent: "flex-end" }}>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                onClick={async () => {
                                                    const isSaved = await handleConfirmCompensation();
                                                    if (!isSaved) return;

                                                    setOpenConfirmDialog(false);
                                                    setIsFinalized(true);

                                                    setTimeout(async () => {
                                                        await fetchEmployeeCompensation();
                                                        setActiveView("preview");
                                                    }, 800);
                                                }}
                                                sx={{ textTransform: "none" }}
                                            >
                                                Confirm
                                            </Button>

                                            <Button
                                                onClick={() => setOpenConfirmDialog(false)}
                                                color="error"
                                                variant="outlined"
                                                sx={{ textTransform: "none" }}
                                            >
                                                Close
                                            </Button>
                                        </DialogActions>
                                    </Dialog>

                                    <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={handlePreviewOpen}
                                            sx={{ textTransform: "none" }}
                                        >
                                            Preview Salary Structure
                                        </Button>
                                    </Box>
                                </Paper>
                            )}

                        <Dialog
                            open={openAddAllowanceConfirm}
                            onClose={() => setOpenAddAllowanceConfirm(false)}
                            maxWidth="xs"
                            fullWidth
                        >
                            <DialogTitle sx={{ fontWeight: 700, color: "#1565c0" }}>
                                Confirm Allowance Addition
                            </DialogTitle>
                            <DialogContent dividers>
                                <Typography>
                                    Do you want to add allowances? Click Yes to add.
                                </Typography>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setOpenAddAllowanceConfirm(false);
                                    }}
                                    sx={{ textTransform: "none" }}
                                >
                                    No
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setOpenAddAllowanceConfirm(false);
                                        setOpenOthers(true);
                                    }}
                                    variant="contained"
                                    sx={{ textTransform: "none" }}
                                >
                                    Yes
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <Dialog
                            open={openOthers}
                            onClose={() => setOpenOthers(false)}
                            maxWidth="xs"
                            fullWidth
                        >
                            <DialogTitle sx={{ pb: 0, fontWeight: 700, color: "#1565c0" }}>
                                Select Allowance Type & Amount
                            </DialogTitle>
                            <DialogContent dividers>
                                <Box sx={{ mt: 2 }}>
                                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                        <InputLabel>Allowance Type</InputLabel>
                                        <Select
                                            value={selectedAllowanceType}
                                            label="Allowance Type"
                                            onChange={(e) => setSelectedAllowanceType(e.target.value)}
                                        >
                                            {allowanceTypes.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {selectedAllowanceType === "Other Allowance" && (
                                        <TextField
                                            label="Specify Other Allowance Type"
                                            type="text"
                                            fullWidth
                                            size="small"
                                            sx={{ mb: 2 }}
                                            value={otherAllowanceType}
                                            onChange={(e) => setOtherAllowanceType(e.target.value)}
                                        />
                                    )}

                                    {selectedAllowanceType && (
                                        <TextField
                                            label="Amount (₹)"
                                            type="number"
                                            inputProps={{ min: 0 }}
                                            fullWidth
                                            size="small"
                                            sx={{ mb: 2 }}
                                            value={allowanceAmount}
                                            onChange={(e) => setAllowanceAmount(e.target.value)}
                                            error={Boolean(allowanceAmountError)}
                                            helperText={allowanceAmountError}
                                        />
                                    )}

                                    {addedAllowances.length > 0 && (
                                        <Table
                                            size="small"
                                            sx={{
                                                mt: 2,
                                                "& th": {
                                                    backgroundColor: "#f9f9f9",
                                                    color: "#555",
                                                    fontWeight: 600,
                                                    fontSize: 14,
                                                    borderBottom: "1px solid #ddd",
                                                },
                                                "& td": {
                                                    fontSize: 13,
                                                    color: "#333",
                                                    borderBottom: "1px solid #ddd",
                                                },
                                            }}
                                        >
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Allowance Type</TableCell>
                                                    <TableCell>Amount (₹)</TableCell>
                                                    <TableCell>Edit</TableCell>
                                                    <TableCell>Delete</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {addedAllowances.map((a, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{a.type}</TableCell>
                                                        <TableCell>{a.amount}</TableCell>
                                                        <TableCell>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleEditAllowance(index)}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteAllowance(index)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button size="small" onClick={handleAddAllowance} sx={{ textTransform: "none" }}>
                                    {isEditingIndex !== null ? "Update" : "Add"}
                                </Button>
                                <Button size="small" onClick={() => setOpenOthers(false)} sx={{ textTransform: "none" }}>
                                    Close
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <Dialog
                            open={openEditAllowances}
                            onClose={() => setOpenEditAllowances(false)}
                            maxWidth="xs"
                            fullWidth
                        >
                            <DialogTitle sx={{ pb: 0, fontWeight: 700, color: "#1565c0" }}>
                                Edit Other Allowances
                            </DialogTitle>

                            <DialogContent dividers>
                                <Box sx={{ mt: 2 }}>
                                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                        <InputLabel>Allowance Type</InputLabel>
                                        <Select
                                            value={editSelectedAllowanceType}
                                            label="Allowance Type"
                                            onChange={(e) =>
                                                setEditSelectedAllowanceType(e.target.value)
                                            }
                                        >
                                            {allowanceTypes.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {editSelectedAllowanceType === "Other Allowance" && (
                                        <TextField
                                            label="Specify Other Allowance Type"
                                            type="text"
                                            fullWidth
                                            size="small"
                                            sx={{ mb: 2 }}
                                            value={editOtherAllowanceType}
                                            onChange={(e) => setEditOtherAllowanceType(e.target.value)}
                                        />
                                    )}

                                    {editSelectedAllowanceType && (
                                        <TextField
                                            label="Amount (₹)"
                                            type="number"
                                            inputProps={{ min: 0 }}
                                            fullWidth
                                            size="small"
                                            sx={{ mb: 2 }}
                                            value={editAllowanceAmount}
                                            onChange={(e) => {
                                                setEditAllowanceAmount(e.target.value);
                                                setEditAllowanceAmountError("");
                                            }}
                                            error={Boolean(editAllowanceAmountError)}
                                            helperText={editAllowanceAmountError}
                                        />
                                    )}

                                    {editAllowances.length > 0 && (
                                        <Table size="small" sx={{ mt: 2 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Allowance Type</TableCell>
                                                    <TableCell>Amount (₹)</TableCell>
                                                    <TableCell>Edit</TableCell>
                                                    <TableCell>Delete</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {editAllowances.map((a, i) => (
                                                    <TableRow key={i}>
                                                        <TableCell>{a.type}</TableCell>
                                                        <TableCell>{a.amount}</TableCell>
                                                        <TableCell>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    setIsEditingIndex(i);
                                                                    setEditSelectedAllowanceType(a.type);
                                                                    setEditAllowanceAmount(a.amount);
                                                                }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    const newList = [...editAllowances];
                                                                    newList.splice(i, 1);
                                                                    setEditAllowances(newList);

                                                                    const newOtherAllowancesDetail = newList.reduce(
                                                                        (acc, curr) => {
                                                                            acc[curr.type] = Number(curr.amount || 0);
                                                                            return acc;
                                                                        },
                                                                        {}
                                                                    );
                                                                    setSelectedEmployeeData((prev) => ({
                                                                        ...prev,
                                                                        otherAllowancesDetail:
                                                                            newOtherAllowancesDetail,
                                                                    }));
                                                                    setShowUpdateButton(true);
                                                                }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </Box>
                            </DialogContent>

                            <DialogActions>
                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={isSubmittingAllowance}
                                    sx={{ textTransform: "none" }}
                                    onClick={async () => {
                                        if (!editSelectedAllowanceType) return;

                                        let type = editSelectedAllowanceType;
                                        if (type === "Other Allowance") {
                                            if (!editOtherAllowanceType.trim()) {
                                                setEditAllowanceAmountError(
                                                    "Please specify the other allowance type."
                                                );
                                                return;
                                            }
                                            type = editOtherAllowanceType.trim();
                                        }

                                        const amt = Number(editAllowanceAmount || 0);
                                        if (amt <= 0 || isNaN(amt)) {
                                            setEditAllowanceAmountError(
                                                "Enter a valid amount greater than 0"
                                            );
                                            return;
                                        }

                                        setIsSubmittingAllowance(true);
                                        setEditAllowanceAmountError("");

                                        try {
                                            const updated = [...editAllowances];
                                            if (isEditingIndex !== null) {
                                                updated[isEditingIndex] = { type, amount: amt };
                                            } else {
                                                updated.push({ type, amount: amt });
                                            }

                                            const allowancesPayload = updated.reduce((acc, curr) => {
                                                const apiKey =
                                                    allowanceKeyMap[curr.type.trim()] ||
                                                    curr.type.toLowerCase().replace(/[^a-z0-9]/gi, "");
                                                acc[apiKey] = curr.amount;
                                                return acc;
                                            }, {});

                                            const payload = {
                                                basicPay: Number(
                                                    selectedEmployeeData?.basicPayMonthly || 0
                                                ),
                                                allowances: allowancesPayload,
                                            };

                                            const result = await postData(API_ENDPOINTS.calculatePayrollAllowances, payload);

                                            if (result?.status !== "SUCCESS") {
                                                setEditAllowanceAmountError(
                                                    result?.message || "Failed to calculate allowances"
                                                );
                                                setIsSubmittingAllowance(false);
                                                return;
                                            }

                                            const results = result.results || {};
                                            const key =
                                                allowanceKeyMap[type.trim()] ||
                                                type.toLowerCase().replace(/[^a-z0-9]/gi, "");
                                            const validation = results[key] || results["other"];

                                            if (validation && validation.isValid === false) {
                                                setEditAllowanceAmountError(validation.errorMessage);
                                                setIsSubmittingAllowance(false);
                                                return;
                                            }

                                            setEditAllowances(updated);
                                            setIsEditingIndex(null);
                                            setEditAllowanceAmount("");
                                            setEditSelectedAllowanceType("");
                                            setEditOtherAllowanceType("");
                                            setEditAllowanceAmountError("");

                                            const newOtherAllowancesDetail = updated.reduce(
                                                (acc, curr) => {
                                                    acc[curr.type] = Number(curr.amount || 0);
                                                    return acc;
                                                },
                                                {}
                                            );
                                            setSelectedEmployeeData((prev) => ({
                                                ...prev,
                                                otherAllowancesDetail: newOtherAllowancesDetail,
                                            }));

                                            setShowUpdateButton(true);
                                        } catch (err) {
                                            setEditAllowanceAmountError(
                                                "Network error — please try again"
                                            );
                                        } finally {
                                            setIsSubmittingAllowance(false);
                                        }
                                    }}
                                >
                                    {isEditingIndex !== null ? "Update" : "Add"}
                                </Button>

                                <Button
                                    size="small"
                                    color="secondary"
                                    onClick={() => setOpenEditAllowances(false)}
                                    sx={{ textTransform: "none" }}
                                >
                                    Close
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <Dialog
                            open={openEditVariable}
                            onClose={() => setOpenEditVariable(false)}
                            maxWidth="xs"
                            fullWidth
                        >
                            <DialogTitle sx={{ pb: 0, fontWeight: 700, color: "#1565c0" }}>
                                Edit Variable Pay
                            </DialogTitle>
                            <DialogContent dividers>
                                <Box sx={{ mt: 2 }}>
                                    <TextField
                                        label="Variable Pay (₹)"
                                        type="text"
                                        size="small"
                                        fullWidth
                                        value={String(editVariablePayValue)}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (/^\d*$/.test(val))
                                                setEditVariablePayValue(val === "" ? 0 : Number(val));
                                        }}
                                    />
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button size="small" onClick={() => setOpenEditVariable(false)} sx={{ textTransform: "none" }}>
                                    Close
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    sx={{ textTransform: "none" }}
                                    onClick={() => {
                                        setVariablePay(editVariablePayValue);
                                        setSelectedEmployeeData((prev) => ({
                                            ...prev,
                                            variablePay: Number(editVariablePayValue),
                                        }));

                                        setShowUpdateButton(true);
                                        setOpenEditVariable(false);
                                    }}
                                >
                                    Save
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <Dialog
                            open={openVariableDialog}
                            onClose={() => setOpenVariableDialog(false)}
                            maxWidth="xs"
                            fullWidth
                        >
                            <DialogTitle sx={{ pb: 0, fontWeight: 700, color: "#1565c0" }}>
                                Set Variable Pay
                            </DialogTitle>
                            <DialogContent dividers>
                                <Box sx={{ mt: 2 }}>
                                    <TextField
                                        label="Variable Pay (₹)"
                                        type="text"
                                        size="small"
                                        fullWidth
                                        value={variablePayDraft}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (/^\d*$/.test(val)) setVariablePayDraft(val);
                                        }}
                                    />
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button size="small" onClick={() => setOpenVariableDialog(false)} sx={{ textTransform: "none" }}>
                                    Close
                                </Button>

                                <Button
                                    size="small"
                                    variant="contained"
                                    sx={{ textTransform: "none" }}
                                    onClick={() => {
                                        const updatedValue = Number(variablePayDraft || 0);
                                        setVariablePay(updatedValue);
                                        setSelectedEmployeeData((prev) => ({
                                            ...prev,
                                            variablePay: updatedValue,
                                            netSalaryMonthly:
                                                Number(prev?.grossEarningsMonthly || 0) -
                                                Number(prev?.totalDeductionsMonthly || 0) +
                                                updatedValue,
                                            annualCTC:
                                                (Number(prev?.grossEarningsAnnual || 0) || 0) +
                                                Number(prev?.totalEmployerContributionsAnnual || 0) +
                                                updatedValue,
                                        }));

                                        setOpenVariableDialog(false);
                                    }}
                                >
                                    Save
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {activeView === "preview" && (
                            <SalaryStructureView
                                data={{
                                    employee: addSelectedEmployee || editSelectedEmployee,
                                    selectedEmployeeData,
                                }}
                                onBack={() => setActiveView("form")}
                                token={localStorage.getItem("token")}
                            />
                        )}

                        <Dialog
                            open={editGrossDialog}
                            onClose={() => setEditGrossDialog(false)}
                            maxWidth="sm"
                            fullWidth
                        >
                            <DialogTitle sx={{ fontWeight: 700, color: "#1565c0" }}>
                                Edit Gross Salary
                            </DialogTitle>

                            <DialogContent dividers>
                                <Box sx={{ mt: 2 }}>
                                    <TextField
                                        label="Gross Salary (₹)"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        required
                                        value={editGrossValue}
                                        onChange={(e) => setEditGrossValue(e.target.value)}
                                        onBlur={() =>
                                            setTouched((prev) => ({ ...prev, gross: true }))
                                        }
                                        error={
                                            touched.gross &&
                                            (!editGrossValue || Number(editGrossValue) <= 0)
                                        }
                                        helperText={
                                            touched.gross &&
                                                (!editGrossValue || Number(editGrossValue) <= 0)
                                                ? "Gross salary is required and must be greater than 0"
                                                : ""
                                        }
                                        sx={{ mb: 2 }}
                                        inputProps={{ min: 0 }}
                                    />

                                    <FormControl
                                        fullWidth
                                        size="small"
                                        sx={{ mb: 2 }}
                                        required
                                        error={touched.changeType && !editChangeType}
                                        onBlur={() =>
                                            setTouched((prev) => ({ ...prev, changeType: true }))
                                        }
                                    >
                                        <InputLabel>Change Type</InputLabel>
                                        <Select
                                            value={editChangeType}
                                            label="Change Type"
                                            onChange={(e) => setEditChangeType(e.target.value)}
                                        >
                                            {grossSalaryChangeTypes.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {touched.changeType && !editChangeType && (
                                            <Typography
                                                variant="caption"
                                                color="error"
                                                sx={{ mt: 0.5, ml: 1 }}
                                            >
                                                Change type is required
                                            </Typography>
                                        )}
                                    </FormControl>

                                    {editChangeType === "Others" && (
                                        <TextField
                                            label="Specify Change Type"
                                            type="text"
                                            fullWidth
                                            size="small"
                                            required
                                            value={editCustomType}
                                            onChange={(e) => setEditCustomType(e.target.value)}
                                            onBlur={() =>
                                                setTouched((prev) => ({ ...prev, customType: true }))
                                            }
                                            error={touched.customType && !editCustomType.trim()}
                                            helperText={
                                                touched.customType && !editCustomType.trim()
                                                    ? "Custom change type is required when selecting 'Others'"
                                                    : ""
                                            }
                                            sx={{ mb: 2 }}
                                        />
                                    )}

                                    <TextField
                                        label="Change Reason"
                                        type="text"
                                        fullWidth
                                        size="small"
                                        required
                                        multiline
                                        rows={3}
                                        sx={{ mb: 2 }}
                                        value={editChangeReason}
                                        onChange={(e) => setEditChangeReason(e.target.value)}
                                        onBlur={() =>
                                            setTouched((prev) => ({ ...prev, reason: true }))
                                        }
                                        error={touched.reason && !editChangeReason.trim()}
                                        helperText={
                                            touched.reason && !editChangeReason.trim()
                                                ? "Change reason is required"
                                                : ""
                                        }
                                    />

                                    <Box sx={{ mt: 2 }}>
                                        <DatePicker
                                            label="Effective From"
                                            value={editEffectiveFrom}
                                            onChange={(newValue) => setEditEffectiveFrom(newValue)}
                                            renderInput={(params) => (
                                                <TextField {...params} size="small" fullWidth />
                                            )}
                                        />
                                    </Box>
                                </Box>
                            </DialogContent>

                            <DialogActions>
                                <Button size="small" onClick={() => setEditGrossDialog(false)} sx={{ textTransform: "none" }}>
                                    Cancel
                                </Button>

                                <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    sx={{ textTransform: "none" }}
                                    onClick={() => {
                                        setTouched({
                                            gross: true,
                                            changeType: true,
                                            customType: true,
                                            reason: true,
                                        });

                                        if (
                                            !editGrossValue ||
                                            Number(editGrossValue) <= 0 ||
                                            !editChangeType ||
                                            !editChangeReason.trim() ||
                                            (editChangeType === "Others" && !editCustomType.trim())
                                        ) {
                                            return;
                                        }

                                        setSelectedEmployeeData((prev) => ({
                                            ...prev,
                                            grossEarningsMonthly: Number(editGrossValue || 0),
                                        }));
                                        setShowUpdateButton(true);
                                        setEditGrossDialog(false);
                                    }}
                                >
                                    Save Changes
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {currentMode === "edit" &&
                            editSelectedEmployee &&
                            selectedEmployeeData &&
                            activeView !== "preview" && (
                                <Box sx={{ mb: 3, borderRadius: 3, p: 3 }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            mb: 2,
                                            color: "#0d47a1",
                                            fontSize: "1.25rem",
                                        }}
                                    >
                                        Basic Information
                                    </Typography>

                                    <Grid container spacing={2} sx={{ mb: 4 }}>
                                        {[
                                            [
                                                "Name",
                                                `${selectedEmployeeData.personal?.first_name} ${selectedEmployeeData.personal?.last_name}`,
                                            ],
                                            ["Employee Id", selectedEmployeeData.personal?.emp_id],
                                            ["Department", selectedEmployeeData.personal?.department],
                                            ["Job Title", selectedEmployeeData.personal?.designation],
                                            [
                                                "Pay Frequency",
                                                selectedEmployeeData?.Pay_Frequency || "-",
                                            ],
                                            [
                                                "Effective From",
                                                selectedEmployeeData?.Effective_From || "-",
                                            ],
                                            [
                                                "Last Increment",
                                                selectedEmployeeData?.Last_Increment || "-",
                                            ],
                                            [
                                                "Variable Pay Cycle",
                                                selectedEmployeeData?.Variable_Pay_Cycle || "-",
                                            ],
                                            [
                                                "Bonus Pay Cycle",
                                                selectedEmployeeData?.Bonus_Pay_Cycle || "-",
                                            ],
                                            [
                                                "Fixed Gross Salary",
                                                <>
                                                    ₹{selectedEmployeeData.grossEarningsMonthly}
                                                    <Button
                                                        size="small"
                                                        sx={{ ml: 1, minWidth: 0, textTransform: "none" }}
                                                        onClick={() => {
                                                            if (isSavedSuccess || isFinalized) {
                                                                setSaveMessage({
                                                                    open: true,
                                                                    text: "Gross salary cannot be edited after confirmation.",
                                                                    severity: "warning",
                                                                });
                                                                return;
                                                            }
                                                            handleOpenEditDialog();
                                                            setShowUpdateButton(true);
                                                        }}
                                                        aria-label="Edit Gross Salary"
                                                        disabled={!selectedEmployeeData.grossEarningsMonthly || isSavedSuccess || isFinalized}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </Button>
                                                </>,
                                            ],
                                            ["Employment Type", selectedEmployeeData.isPartTime ? "Part-time" : "Full-time"],
                                            ["Working Hours", selectedEmployeeData.isPartTime ? `${selectedEmployeeData.workingHoursPerDay} hrs/day` : "8 hrs/day"],
                                        ].map(([label, value]) => (
                                            <Grid size={{ xs: 6 }} key={label}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        gap: 0.5,
                                                        alignItems: "center",
                                                        flexWrap: "nowrap",
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: "1rem",
                                                            color: "#333",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {label}:
                                                    </Typography>
                                                    <Box sx={{ fontSize: "1rem", color: "#555" }}>
                                                        {value}
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>

                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            mb: 2,
                                            color: "#0d47a1",
                                            fontSize: "1.25rem",
                                        }}
                                    >
                                        Salary Structure
                                    </Typography>

                                    <Grid container spacing={2} sx={{ mb: 4 }}>
                                        {[
                                            ["Basic Pay", selectedEmployeeData.basicPayMonthly || 0],
                                            ["HRA", selectedEmployeeData.hraMonthly || 0],
                                            [
                                                "Conveyance Allowance",
                                                selectedEmployeeData.conveyanceAllowanceMonthly || 0,
                                            ],
                                            [
                                                "Special Allowance",
                                                selectedEmployeeData.specialAllowanceMonthly || 0,
                                            ],
                                        ].map(([label, value]) => (
                                            <Grid size={{ xs: 6 }} key={label}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        gap: 0.5,
                                                        alignItems: "center",
                                                        flexWrap: "nowrap",
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: "1rem",
                                                            color: "#333",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {label}:
                                                    </Typography>
                                                    <Typography sx={{ fontSize: "1rem", color: "#555" }}>
                                                        ₹
                                                        {Number(value).toLocaleString("en-IN", {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        })}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}

                                        {selectedEmployeeData.otherAllowancesDetail &&
                                            Object.keys(selectedEmployeeData.otherAllowancesDetail).length > 0 ? (
                                            <Grid size={{ xs: 12 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Typography sx={{ fontWeight: 600 }}>Other Allowances:</Typography>
                                                    <Box
                                                        onClick={() => {
                                                            setOpenEditAllowances(true);
                                                            setShowUpdateButton(true);
                                                        }}
                                                        sx={{ cursor: "pointer" }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </Box>
                                                </Box>

                                                <Box sx={{ mt: 0.5, ml: 2 }}>
                                                    {Object.entries(selectedEmployeeData.otherAllowancesDetail).map(
                                                        ([type, value], idx) => {
                                                            if (typeof value === "object" && value !== null) {
                                                                return (
                                                                    <Typography key={idx} sx={{ color: "#555" }}>
                                                                        {type}: ₹
                                                                        {Number(value.amount || 0).toLocaleString("en-IN", {
                                                                            minimumFractionDigits: 2,
                                                                            maximumFractionDigits: 2,
                                                                        })}{" "}
                                                                        {value.type ? `(Type: ${value.type})` : ""}
                                                                    </Typography>
                                                                );
                                                            }
                                                            return (
                                                                <Typography key={idx} sx={{ color: "#555" }}>
                                                                    {type}: ₹
                                                                    {Number(value || 0).toLocaleString("en-IN", {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2,
                                                                    })}
                                                                </Typography>
                                                            );
                                                        }
                                                    )}
                                                </Box>
                                            </Grid>
                                        ) : (
                                            <Grid size={{ xs: 12 }}>
                                                <Typography sx={{ color: "#777", mt: 0.5, ml: 2 }}>
                                                    No additional allowances
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>

                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            mb: 2,
                                            color: "#0d47a1",
                                            fontSize: "1.25rem",
                                        }}
                                    >
                                        Deductions
                                    </Typography>

                                    <Grid container spacing={2} sx={{ mb: 4 }}>
                                        {[
                                            ["Provident Fund (PF)", selectedEmployeeData.pfEmployeeMonthly || 0],
                                            ["Employee State Insurance (ESI)", selectedEmployeeData.esiEmployeeMonthly || 0],
                                            ["Professional Tax (PT)", selectedEmployeeData.professionalTaxMonthly || 0],
                                            ["Tax Deducted at Source (TDS)", selectedEmployeeData.monthlyTDS || 0],
                                        ].map(([label, value]) => (
                                            <Grid size={{ xs: 6 }} key={label}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        gap: 0.5,
                                                        alignItems: "center",
                                                        flexWrap: "nowrap",
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: "1rem",
                                                            color: "#333",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {label}:
                                                    </Typography>
                                                    <Typography sx={{ fontSize: "1rem", color: "#555" }}>
                                                        ₹
                                                        {Number(value).toLocaleString("en-IN", {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        })}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>

                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            mb: 2,
                                            color: "#0d47a1",
                                            fontSize: "1.25rem",
                                        }}
                                    >
                                        Employer Contributions
                                    </Typography>
                                    <Grid container spacing={2} sx={{ mb: 4 }}>
                                        {[
                                            [
                                                "Employer Provident Fund (PF)",
                                                selectedEmployeeData.pfEmployerMonthly || 0,
                                            ],
                                            [
                                                "Employer ESI",
                                                selectedEmployeeData.employerContributions?.esi || 0,
                                            ],
                                        ].map(([label, value]) => (
                                            <Grid size={{ xs: 6 }} key={label}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        gap: 0.5,
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <Typography sx={{ fontWeight: 600, color: "#333" }}>
                                                        {label}:
                                                    </Typography>
                                                    <Typography sx={{ color: "#555" }}>₹{value}</Typography>
                                                </Box>
                                            </Grid>
                                        ))}

                                        {Number(selectedEmployeeData.variablePay || 0) > 0 && (
                                            <Grid size={{ xs: 6 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Typography sx={{ fontWeight: 600 }}>Variable Pay:</Typography>
                                                    <Typography>
                                                        ₹
                                                        {Number(selectedEmployeeData.variablePay).toLocaleString("en-IN", {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        })}
                                                    </Typography>
                                                    <Box
                                                        onClick={() => {
                                                            setOpenEditVariable(true);
                                                            setShowUpdateButton(true);
                                                        }}
                                                        sx={{ cursor: "pointer" }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>

                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            mb: 2,
                                            color: "#0d47a1",
                                            fontSize: "1.25rem",
                                        }}
                                    >
                                        Summary
                                    </Typography>

                                    <Grid container spacing={2} sx={{ mb: 4 }}>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography sx={{ fontWeight: 600 }}>Gross Salary:</Typography>
                                            <Typography sx={{ color: "#555" }}>
                                                ₹
                                                {Number(selectedEmployeeData.grossEarningsMonthly || 0)
                                                    .toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </Typography>
                                        </Grid>

                                        <Grid size={{ xs: 6 }}>
                                            <Typography sx={{ fontWeight: 600 }}>Total Deductions:</Typography>
                                            <Typography sx={{ color: "#555" }}>
                                                ₹
                                                {Number(selectedEmployeeData.totalDeductionsMonthly || 0)
                                                    .toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </Typography>
                                        </Grid>

                                        <Grid size={{ xs: 6 }}>
                                            <Typography sx={{ fontWeight: 600 }}>Net Salary:</Typography>
                                            <Typography sx={{ color: "#555" }}>
                                                ₹
                                                {Number(selectedEmployeeData.netSalaryMonthly || 0)
                                                    .toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </Typography>
                                        </Grid>

                                        <Grid size={{ xs: 6 }}>
                                            <Typography sx={{ fontWeight: 600 }}>Annual CTC:</Typography>
                                            <Typography sx={{ color: "#555" }}>
                                                ₹
                                                {Number(selectedEmployeeData.annualCTC || 0)
                                                    .toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    {showUpdateButton && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            onClick={handleUpdateCompensation}
                                            sx={{ mt: 2, mr: 2, textTransform: "none" }}
                                        >
                                            Update Compensation
                                        </Button>
                                    )}

                                    <Divider sx={{ my: 2 }} />
                                    <Button
                                        size="small"
                                        variant="contained"
                                        sx={{ textTransform: "none" }}
                                        onClick={() => setActiveView("preview")}
                                    >
                                        Preview Salary Structure
                                    </Button>
                                </Box>
                            )}

                        <Snackbar
                            open={saveMessage.open}
                            autoHideDuration={6000}
                            onClose={() => setSaveMessage({ ...saveMessage, open: false })}
                            anchorOrigin={{ vertical: "top", horizontal: "center" }}
                        >
                            <Alert
                                onClose={() => setSaveMessage({ ...saveMessage, open: false })}
                                severity={saveMessage.severity}
                                sx={{ width: "100%" }}
                            >
                                {saveMessage.text}
                            </Alert>
                        </Snackbar>
                    </CardContent>
                </Card>
            </LocalizationProvider>
        </Box>
    );
}

export default HRCompensationTab;